import { action, computed, observable } from 'mobx';
import { isNaN, remove } from 'lodash';
import type { RootStore } from '~/stores/root.store';
import {
    IFlowTemplate,
    IFolder,
    LeadFormStatus,
    ITemplateBase,
    ClientReferralsConfig
} from '~/types/model.types';
import {
    IFlowResponse,
    TRequestStatus,
    IAIOnboardingTemplates
} from '~/types/server.types';
import {
    TemplatePanel,
    TFileTemplatePanel,
    TSortBy,
    TTemplatePanel,
    FileTemplatePanel,
    TTemplateType
} from '~/components/Templates/types/templates.types';
import { IFlowGalleryTemplate } from '~/components/FlowGallery/types/flow-gallery.types';
import {
    IFlowFilterByAction,
    TFlowActionType
} from '~/components/CreateFlow/types/create-flow.types';
import {
    IPushToGalleryTemplatesData,
    PUSH_TO_GALLERY_TEMPLATES_NUMBER
} from '~/services/flow-gallery.service';
import { UiPersistenceStore } from '~/stores/ui-persistence.store';
import { trackError } from '~/services/logger/error.utils';

export class TemplatesUiStore {
    rootStore: RootStore = null;
    @observable activeFlowPanel: TTemplatePanel | string = null; // flows
    @observable activeFileTemplatePanel: TFileTemplatePanel = null; // non-flows
    @observable flowTemplates: IFlowTemplate[] = [];
    @observable _leadForms: IFlowTemplate[] = [];
    @observable _leadFormTemplates: IFlowGalleryTemplate[] = [];
    @observable clientReferralsConfig: ClientReferralsConfig = {
        isEligible: false,
        isEnabled: false
    };
    @observable _aiOnboardingTemplates: IAIOnboardingTemplates[] = [];
    @observable fileTemplates: any[] = [];
    @observable folders: IFolder[] = [];
    @observable selectedTemplate: IFlowTemplate = null;
    @observable requestStatus: TRequestStatus = 'getting_ui_persistence';
    @observable isFolderPanelActive: boolean = false;
    @observable isNewlyCreatedTemplate: boolean = false;
    @observable maxVisibleFolders: number = 10;
    @observable shouldShowMoreFolders: boolean = false;
    @observable isFilterByInvoice: boolean = false;
    @observable isFilterByRecurringInvoice: boolean = false;
    @observable isFilterByContract: boolean = false;
    @observable isFilterByQuestion: boolean = false;
    @observable isFilterByServices: boolean = false;
    @observable isFilterByScheduler: boolean = false;
    @observable isFolderModalOpen: boolean = false;
    @observable isDeleteFlowWarningOpen: boolean = false;
    @observable isSecondFolderTooltip: string = '';
    @observable isGridView: boolean = true;
    @observable pushToGalleryTemplates: IFlowGalleryTemplate[];
    @observable isShareModalOpen: boolean;
    @observable sharedWithEmail: string;
    @observable isLoadingStatus: boolean;
    @observable sharedTemplate: IFlowResponse;
    @observable sharedTemplateMetadataId: string;
    @observable sharedTemplateError: string = null;
    @observable isCustomFontsWarning: boolean;
    @observable isEmailNotValid = false;
    @observable searchQuery: string = '';
    @observable legacyTemplatesVisiblePanels: TFileTemplatePanel[] = [];

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.uiPersistenceStore = rootStore.uiPersistenceStore;
    }

    private uiPersistenceStore: UiPersistenceStore;

    @action.bound
    setSharedTemplate(template: IFlowResponse) {
        this.sharedTemplate = template;
    }

    @action.bound
    setIsCustomFontsWarning(isCustomFontsWarning: boolean) {
        this.isCustomFontsWarning = isCustomFontsWarning;
    }

    @action.bound
    setSharedTemplateMetadataId(metadataId: string) {
        this.sharedTemplateMetadataId = metadataId;
    }

    @action.bound
    setSharedTemplatePermissionsError(err: string) {
        this.sharedTemplateError = err;
    }

    @action.bound
    setSearchQuery(query: string) {
        this.searchQuery = query;
    }

    @action
    setFolderModalOpen(val: boolean) {
        this.isFolderModalOpen = val;
    }

    @action.bound
    setIsSecondFolderTooltip(folderId: string) {
        this.isSecondFolderTooltip = folderId;
    }

    @action
    setDeleteFlowWarningOpen(val: boolean) {
        this.isDeleteFlowWarningOpen = val;
    }

    @action
    setLayoutViewMode(val: boolean) {
        this.isGridView = val;
    }

    @action.bound
    setIsNewlyCreatedTemplate(state: boolean) {
        this.isNewlyCreatedTemplate = state;
    }

    @action
    setRequestStatus(val: TRequestStatus) {
        this.requestStatus = val;
    }

    @action
    setActivePanel(panel: TTemplatePanel) {
        this.activeFlowPanel = panel;
        this.isFolderPanelActive = false;
        this.activeFileTemplatePanel = null;
    }

    @action.bound
    setActiveFolderPanel(folderId: string) {
        this.activeFlowPanel = folderId;
        this.isFolderPanelActive = true;
        this.activeFileTemplatePanel = null;
    }

    @action
    setActiveFileTemplatePanel(panel: TFileTemplatePanel) {
        this.activeFlowPanel = null;
        this.activeFileTemplatePanel = panel;
    }

    @action
    setSortTemplates(val: TSortBy) {
        this.uiPersistenceStore.set('sortTemplatesBy.value', val);
    }

    @action.bound
    loadFlowTemplates(templates: IFlowTemplate[]) {
        this.flowTemplates = templates;
    }

    @action.bound
    loadPublicFlows(templates: IFlowTemplate[]) {
        this._leadForms = templates;
    }

    @action.bound
    loadLeadFormTemplates(templates: IFlowGalleryTemplate[]) {
        this._leadFormTemplates = templates;
    }

    @action.bound
    setClientReferralsConfig(config: ClientReferralsConfig) {
        this.clientReferralsConfig = config;
    }

    @action.bound
    loadAiOnboardingTemplates(templates: IAIOnboardingTemplates[]) {
        this._aiOnboardingTemplates = templates;
    }

    @action.bound
    loadFolders(folders: IFolder[]) {
        this.folders = folders;
    }

    @action.bound
    addNewFolder(folder: IFolder) {
        this.folders.push(folder);
    }

    @action.bound
    deleteFolder(folder: IFolder) {
        this.folders = this.folders.filter(i => i._id !== folder._id);
    }

    @action.bound
    updateFolder(folder: IFolder) {
        this.folders = this.folders.map(i => {
            if (i._id === folder._id) {
                i.path = folder.path;
            }
            return i;
        });
    }

    @action.bound
    updateFolderName(folder: IFolder) {
        this.flowTemplates = this.flowTemplates.map(i => {
            if (i.folder_id === folder._id) {
                i.path = folder.path;
            }
            return i;
        });
        this.updateFolder(folder);
    }

    @action.bound
    updateTemplatePublicConfiguration(
        narrowTemplate: Pick<
            IFlowTemplate,
            '_id' | 'title' | 'thumbnail_image' | 'public_configuration'
        >
    ) {
        const template = this.flowTemplates.find(
            flowTemplate => flowTemplate._id === narrowTemplate._id
        );
        template.public_configuration = narrowTemplate.public_configuration;
        this.updateSelectedTemplate(template);
        this.updateTemplates(template);
    }

    @action.bound
    updateSelectedTemplate(template: IFlowTemplate) {
        this.selectedTemplate = template;
    }

    @action.bound
    updateTemplates(template: IFlowTemplate) {
        this.flowTemplates = this.flowTemplates.map(flowTemplate =>
            flowTemplate._id === template._id ? template : flowTemplate
        );
    }

    @action.bound
    setIsEmailNotValid(isNotValid: boolean) {
        this.isEmailNotValid = isNotValid;
    }

    @action.bound
    addFlowTemplate(template: IFlowTemplate) {
        this.flowTemplates.unshift(template);
    }

    @action.bound
    addPublicFlowTemplate(template: IFlowTemplate) {
        this._leadForms.unshift(template);
    }

    @action.bound
    getPublicFlowTemplate(templateId: string) {
        return this.leadFormTemplates.find(
            template => template.published_template_id === templateId
        );
    }

    @computed
    get sortByTemplate(): TSortBy {
        return this.uiPersistenceStore.get('sortTemplatesBy.value', 'date-asc');
    }

    // Folders
    @computed
    get selectedFolderName() {
        if (!this.activeFlowPanel || this.activeFlowPanel in TemplatePanel) {
            return null;
        }
        return this.folders?.find(fldr => fldr._id === this.activeFlowPanel)
            ?.path;
    }

    @computed
    get folderTemplates() {
        return this.isFolderPanelActive
            ? this.flowTemplates.filter(
                  i => i.folder_id === this.activeFlowPanel
              )
            : [];
    }

    @computed
    get isFolderDeletable() {
        return this.folderTemplates.length === 0;
    }

    @computed
    get sortedCompanyFolders() {
        return this.folders.slice().sort((a, b) => a.order - b.order);
    }

    @computed
    get madeForMeFolder() {
        return this.folders.find(folder => folder.path === 'Made for me');
    }

    // Flow template List
    get isFlowPanel() {
        return this.activeFlowPanel !== null;
    }

    @computed
    get starredTemplates() {
        return this.flowTemplates.filter(i => i.starred);
    }

    @computed
    get sharedTemplates() {
        return this.flowTemplates.filter(
            template => template.public_configuration?.published_template
        );
    }

    @computed
    get myFlows() {
        return this.flowTemplates.filter(i => i.folder_id === null);
    }

    @computed
    get leadForms() {
        return this._leadForms;
    }

    @computed
    get leadFormTemplates() {
        return this._leadFormTemplates;
    }

    @computed
    get aiOnboardingTemplates() {
        return this._aiOnboardingTemplates;
    }

    @computed
    get madeForMeTemplates() {
        return this.flowTemplates.filter(
            template => template.path === 'made for me'
        );
    }

    @computed
    get isLeadFormsEmpty() {
        return (
            !this.isLoading &&
            !this.isInitialLoading &&
            this.leadForms.length === 0 &&
            !this.flowFilterTypes.activeFilters.length
        );
    }

    @computed
    get isLeadFormsEmptyState() {
        return this.isLeadFormPanelActive && this.isLeadFormsEmpty;
    }

    @computed
    get isFilterSectionShown() {
        return this.isFlowPanel && !this.isLeadFormsEmptyState;
    }

    @computed
    get isLeadFormPanelActive() {
        return this.activeFlowPanel === TemplatePanel.Lead_forms;
    }

    @computed
    get isInitialLoading() {
        return (
            this.requestStatus === 'getting_ui_persistence' ||
            this.requestStatus === 'initial_load'
        );
    }

    get isLoading() {
        return (
            this.requestStatus === 'initial_load' ||
            this.requestStatus === 'fetching'
        );
    }

    getSortedTemplates<T extends IFlowTemplate>(
        sortType: TSortBy,
        templates: T[]
    ): T[] {
        const list: T[] = templates || [];

        switch (sortType) {
            case 'date-desc':
                return list
                    .slice()
                    .sort(
                        (x, y) =>
                            +new Date(x.created_at) - +new Date(y.created_at)
                    );
            case 'date-asc':
                return list
                    .slice()
                    .sort(
                        (x, y) =>
                            +new Date(y.created_at) - +new Date(x.created_at)
                    );
            default:
                return list
                    .slice()
                    .sort((a, b) =>
                        a.title?.toLowerCase() < b.title?.toLowerCase() ? -1 : 1
                    );
        }
    }

    @action.bound
    setFlowFilterType(filterKey: TFlowActionType) {
        switch (filterKey) {
            case 'questionnaire':
                this.isFilterByQuestion = !this.isFilterByQuestion;
                break;
            case 'contract':
                this.isFilterByContract = !this.isFilterByContract;
                break;
            case 'invoice':
                this.isFilterByInvoice = !this.isFilterByInvoice;
                break;
            case 'services':
                this.isFilterByServices = !this.isFilterByServices;
                break;
            case 'scheduler':
                this.isFilterByScheduler = !this.isFilterByScheduler;
                break;
            case 'recurring_invoice':
                this.isFilterByRecurringInvoice =
                    !this.isFilterByRecurringInvoice;
                break;
            default:
                throw Error('Invalid filter key');
        }
    }

    @computed
    get activePanel() {
        const isActive = (val: TTemplatePanel) => this.activeFlowPanel === val;
        return {
            isAllFlows: isActive(TemplatePanel.All_flows),
            isStarred: isActive(TemplatePanel.Starred),
            isShared: isActive(TemplatePanel.Starred),
            isMyFlows: isActive(TemplatePanel.My_flows),
            isLeadForms: isActive(TemplatePanel.Lead_forms),
            isFolder: this.isFolderPanelActive
        };
    }

    @computed
    get flowFilterTypes() {
        const activeFilters: TFlowActionType[] = [];
        const { companyStore } = this.rootStore;
        const isRecurringInvoiceDiscoverabilityFlagOpen =
            companyStore.currCompany?.isNev;

        const list: IFlowFilterByAction[] = [
            {
                key: 'invoice',
                label: 'Invoice',
                isActive: this.isFilterByInvoice
            },
            ...(isRecurringInvoiceDiscoverabilityFlagOpen
                ? [
                      {
                          key: 'recurring_invoice' as const,
                          label: 'Recurring invoice',
                          isActive: this.isFilterByRecurringInvoice
                      }
                  ]
                : []),
            {
                key: 'contract',
                label: 'Contract',
                isActive: this.isFilterByContract
            },
            {
                key: 'questionnaire',
                label: 'Questions',
                isActive: this.isFilterByQuestion
            },
            {
                key: 'services',
                label: 'Services',
                isActive: this.isFilterByServices
            },
            {
                key: 'scheduler',
                label: 'Scheduler',
                isActive: this.isFilterByScheduler
            }
        ];

        if (this.activePanel.isLeadForms) {
            const leadFormsFilters: TFlowActionType[] = [
                'questionnaire',
                'services',
                'scheduler',
                'invoice'
            ];
            remove(list, item => leadFormsFilters.indexOf(item.key) === -1);
        }

        list.forEach(i => {
            if (i.isActive) {
                activeFilters.push(i.key);
                return;
            }
            const index = activeFilters.indexOf(i.key);

            if (index > -1) {
                activeFilters.splice(index, 1);
            }
        });

        return { list, activeFilters };
    }

    @computed
    get resultDescription() {
        if (this.activeFlowTemplateList.length === 0) {
            return '';
        }
        if (this.flowFilterTypes.activeFilters.length === 0) {
            return `${this.activeFlowTemplateList.length} Smart Files templates`;
        }

        if (this.activeFlowTemplateList.length > 0) {
            return `${
                this.activeFlowTemplateList.length
            } templates with ${this.flowFilterTypes.activeFilters.join(
                ' and '
            )}`;
        }
        return null;
    }

    // Flow templates
    @computed
    get panelFlowTemplates() {
        switch (this.activeFlowPanel) {
            case TemplatePanel.All_flows:
                return this.flowTemplates;
            case TemplatePanel.Starred:
                return this.starredTemplates;
            case TemplatePanel.My_flows:
                return this.myFlows;
            case TemplatePanel.Lead_forms:
                return this._leadForms;
            case TemplatePanel.Shared:
                return this.sharedTemplates;
            default:
                return this.folderTemplates;
        }
    }

    @computed
    get activeFlowTemplateList() {
        return this.getSortedTemplates(
            this.sortByTemplate,
            this.panelFlowTemplates
        );
    }

    @computed
    get publishedLeadFormsCount() {
        return this.activeFlowTemplateList.filter(
            flowTemplate =>
                flowTemplate.public_flow_status === LeadFormStatus.Live
        )?.length;
    }

    @computed
    get maxPublicLeadFormsAllowedByTier() {
        const { currUser } = this.rootStore.userStore;

        // Ex-trialers and pre-trialers:
        if (
            currUser.isAtwsPreTrialVendor ||
            currUser.hasTrialExpiredDueToNoPayment
        ) {
            return null;
        }

        const leadFormsFeatureVariation = currUser.account.featurePlan?.find(
            feature => feature.key === 'lead_forms'
        )?.variation;
        return !isNaN(+leadFormsFeatureVariation)
            ? +leadFormsFeatureVariation
            : null;
    }

    @action.bound
    starFlowTemplate(flowId: string, starred: boolean) {
        this.flowTemplates = this.flowTemplates.map(i =>
            i._id === flowId ? { ...i, starred } : i
        );
    }

    @action.bound
    renameFlowTemplate(flowId: string, newName: string) {
        const flowToRename = this.flowTemplates.find(i => i._id === flowId);
        if (flowToRename) {
            flowToRename.title = newName;
        }
    }

    @action.bound
    deleteFlowTemplate(flowId: string) {
        this.flowTemplates = this.flowTemplates.filter(i => i._id !== flowId);
    }

    @action.bound
    deletePublicFlow(flowId: string) {
        this._leadForms = this._leadForms.filter(i => i._id !== flowId);
    }

    // Legacy and Non flow templates
    @action
    setActiveFileTemplates(templates) {
        this.fileTemplates = templates;
    }

    @computed
    get activeFileTemplateList() {
        return this.getSortedTemplates(this.sortByTemplate, this.fileTemplates);
    }

    @action.bound
    setShowMoreFolders(shouldShow: boolean) {
        this.shouldShowMoreFolders = shouldShow;
    }

    @action.bound
    setPushToGalleryTemplates({
        flows,
        fallbackFlow
    }: IPushToGalleryTemplatesData) {
        this.pushToGalleryTemplates = flows || [];
        if (
            this.pushToGalleryTemplates.length <
                PUSH_TO_GALLERY_TEMPLATES_NUMBER &&
            fallbackFlow
        ) {
            this.pushToGalleryTemplates.push(fallbackFlow);
        }
    }

    @action.bound
    resetActiveFilters() {
        this.isFilterByInvoice = false;
        this.isFilterByContract = false;
        this.isFilterByQuestion = false;
        this.isFilterByServices = false;
        this.isFilterByScheduler = false;
        this.isFilterByRecurringInvoice = false;
    }

    @action.bound
    openShareModal() {
        this.isShareModalOpen = true;
    }

    @action.bound
    closeShareModal() {
        this.isShareModalOpen = false;
        this.selectedTemplate = undefined;
        this.editSharedWithEmail(null);
        this.setIsCustomFontsWarning(null);
        this.setIsEmailNotValid(false);
    }

    @action.bound
    editSharedWithEmail(email: string) {
        this.sharedWithEmail = email;
    }

    @action.bound
    setIsLoadingStatus(status: boolean) {
        this.isLoadingStatus = status;
    }

    @action.bound
    togglePublicFlowTemplateStatus(flowId: string, isActive: boolean) {
        this._leadForms.find(flow => {
            if (flow._id === flowId) {
                flow.public_flow_status = isActive
                    ? LeadFormStatus.Live
                    : LeadFormStatus.Inactive;
            }
        });
    }

    @action.bound
    setLegacyTemplatesVisiblePanels(panels: TFileTemplatePanel[]) {
        this.legacyTemplatesVisiblePanels = panels;
    }

    @action
    reset() {
        this.requestStatus = 'getting_ui_persistence';
        this.selectedTemplate = null;
        this.flowTemplates = [];
        this.fileTemplates = [];
        this.activeFlowPanel = null;
        this.activeFileTemplatePanel = null;
        this.resetActiveFilters();
        this.isFolderModalOpen = false;
        this.isDeleteFlowWarningOpen = false;
        this.isSecondFolderTooltip = '';
        this.setIsEmailNotValid(false);
    }

    getLegacyTemplatesPerPanel(panel: TFileTemplatePanel) {
        const {
            userStore: { currUser },
            companyStore: {
                currCompany: {
                    emailTemplates,
                    packagesTemplates,
                    contactFormsTemplates,
                    giftCardsTemplates,
                    brochuresTemplates,
                    invoices,
                    agreements,
                    proposals,
                    questionnaires,
                    flows
                }
            }
        } = this.rootStore;

        const templatesPerPanel: Record<
            Exclude<TFileTemplatePanel, 'All'>,
            ITemplateBase[]
        > = {
            [FileTemplatePanel.Invoices]: invoices ?? [],
            [FileTemplatePanel.Agreements]: agreements ?? [],
            [FileTemplatePanel.Proposals]: proposals ?? [],
            [FileTemplatePanel.Emails]: emailTemplates ?? [],
            [FileTemplatePanel.Packages]: packagesTemplates ?? [],
            [FileTemplatePanel.Contact_forms]: contactFormsTemplates ?? [],
            [FileTemplatePanel.Gift_cards]: giftCardsTemplates ?? [],
            [FileTemplatePanel.Email_signature]:
                currUser.email_signatures ?? [],
            [FileTemplatePanel.Brochures]: brochuresTemplates ?? [],
            [FileTemplatePanel.Questionnaires]: questionnaires ?? [],
            [FileTemplatePanel.Blocks]: flows ?? []
        };

        let templates: ITemplateBase[];
        if (panel === FileTemplatePanel.All) {
            templates = this.legacyTemplatesVisiblePanels.reduce(
                (acc, visiblePanel: FileTemplatePanel) => {
                    const formattedTemplates = templatesPerPanel[
                        visiblePanel
                    ].map(template => ({ ...template, type: visiblePanel }));
                    return acc.concat(formattedTemplates);
                },
                []
            );
        } else {
            templates = templatesPerPanel[panel].map(template => ({
                ...template,
                type: panel as TTemplateType //TODO: double check this
            }));
        }

        return templates.filter(template => {
            if (!template._id) {
                trackError({
                    context: { template },
                    error: new Error('Missing _id on file template')
                });
                return false;
            }
            return true;
        });
    }
}
